import React, { useContext } from "react";
import { Eyebrow } from "@bloomberg/media-ui-article";
import styles from "./quotePageLayout.module.css";
import { QuoteContext } from "~context/QuoteContext";
import { isTickerActive } from "~utils/tickerStatus";
import assertNumericValue from "~utils/assertNumericValue";
import QuoteSearch from "~components/QuoteSearch";
import QuotePageHeader from "~components/QuotePageHeader";
import CurrentPrice from "~components/CurrentPrice";
import MarketLastUpdate from "~components/MarketLastUpdate";
import AfterHoursPrice from "~components/AfterHoursPrice";
import { TickerStatus } from "~components/TickerStatus";
import { useTranslateMessage } from "~utils/i18n";
import { useLocale } from "~hooks/i18n/useLocale";

const AFTER_HOURS_ENABLED =
  process.env.NEXT_PUBLIC_AFTER_HOURS_ENABLED || "false";

export default function TopSection() {
  const quoteData = useContext(QuoteContext);
  const translateMessage = useTranslateMessage();
  const { locale } = useLocale();

  if (!quoteData) {
    return null;
  }

  const {
    isAfterHours,
    searchClientURL,
    isOpen,
    isExchangeDelayed,
    lastUpdate,
    longName,
    shortName,
    type,
    mediaSecurityType,
    id,
    primaryExchange,
    issuedCurrency,
    price,
    percentChange1Day,
    priceChange1Day,
    lastPostSessionPrice,
    netChange1DayPostSessionRegular,
    percentChange1DayPostRegular,
    marketStatus,
    companyIsPrivate,
    ultimateParentTicker,
    primarySecurityTicker,
    issuerEquityTicker,
    issuedUnits,
    isRealTime,
    exchangeId,
    countryCode,
  } = quoteData;

  const isActive = isTickerActive(marketStatus);

  const showAfterHours =
    isAfterHours &&
    assertNumericValue(lastPostSessionPrice) &&
    assertNumericValue(percentChange1DayPostRegular) &&
    assertNumericValue(netChange1DayPostSessionRegular);

  const marketLabel = translateMessage({
    id: "markets",
    defaultMessage: "Markets",
  });

  const dataLabel = translateMessage({
    id: "data",
    defaultMessage: "Data",
  });

  const sectionTitleLink = locale === "ja" ? "/jp/markets" : "/markets";

  return (
    <div>
      <QuoteSearch
        searchURL={searchClientURL}
        countryCode={countryCode}
        className={styles.mobile}
      />
      <div className={styles.eyebrowContainer}>
        <Eyebrow
          className={styles.eyebrow}
          sectionTitle={marketLabel}
          subsectionTitle={dataLabel}
          sectionTitleLink={sectionTitleLink}
        />
        <QuoteSearch
          searchURL={searchClientURL}
          countryCode={countryCode}
          className={styles.tablet}
        />
      </div>

      <QuotePageHeader
        isOpen={isOpen}
        isExchangeDelayed={isExchangeDelayed}
        lastUpdate={lastUpdate}
        longName={longName}
        shortName={shortName}
        type={type}
        mediaSecurityType={mediaSecurityType}
        companyIsPrivate={Boolean(companyIsPrivate)}
        id={id}
        primaryExchange={primaryExchange}
        issuedCurrency={issuedCurrency}
      />

      {isActive ? (
        <div
          className={
            showAfterHours && AFTER_HOURS_ENABLED === "true"
              ? styles.currentPrice
              : styles.currentPriceEmptyAfterHours
          }
        >
          <CurrentPrice
            currentPrice={price}
            percentChange={percentChange1Day}
            priceChange={priceChange1Day}
            issuedUnits={issuedUnits}
            issuedCurrency={issuedCurrency}
            isRealTime={isRealTime}
          />

          <MarketLastUpdate
            isOpen={isOpen}
            isExchangeDelayed={isExchangeDelayed}
            lastUpdate={lastUpdate}
            lastPostSessionPrice={lastPostSessionPrice}
            percentChange1DayPostRegular={percentChange1DayPostRegular}
            netChange1DayPostSessionRegular={netChange1DayPostSessionRegular}
            isRealTime={isRealTime}
            exchangeId={exchangeId}
          />

          {showAfterHours && (
            <AfterHoursPrice
              currentPrice={lastPostSessionPrice}
              percentChange={percentChange1DayPostRegular}
              priceChange={netChange1DayPostSessionRegular}
            />
          )}
        </div>
      ) : (
        <TickerStatus
          marketStatus={marketStatus}
          tickerId={id}
          ultimateParentTicker={ultimateParentTicker}
          primarySecurityTicker={primarySecurityTicker}
          issuerEquityTicker={issuerEquityTicker}
        />
      )}
    </div>
  );
}
